import { useEffect, useRef, useState } from 'react'

const POLL_INTERVAL_MS = 5 * 60 * 1000 // 5 minutes
const VISIBILITY_DEBOUNCE_MS = 500

export interface UseVersionCheckResult {
  updateAvailable: boolean
  reload: () => void
}

/**
 * Detects when a newer build is live on the server and prompts the SPA to reload.
 *
 * Reads the baked-in build id from `import.meta.env.VITE_APP_VERSION` and polls
 * `GET /version.json` every 5 minutes plus whenever the tab becomes visible.
 * On mismatch, flips `updateAvailable` to `true` and stops polling — no need
 * to keep firing once we already know a refresh is needed. Fetch errors are
 * swallowed so transient network issues don't spam or false-positive.
 *
 * @returns Current update state plus a `reload` handler bound to `window.location.reload`.
 */
export function useVersionCheck(): UseVersionCheckResult {
  const [updateAvailable, setUpdateAvailable] = useState(false)
  const bakedVersion = import.meta.env.VITE_APP_VERSION
  const stoppedRef = useRef(false)
  const lastCheckAtRef = useRef(0)

  useEffect(() => {
    if (!bakedVersion) return

    let cancelled = false

    async function check() {
      if (stoppedRef.current || cancelled) return
      const now = Date.now()
      if (now - lastCheckAtRef.current < VISIBILITY_DEBOUNCE_MS) return
      lastCheckAtRef.current = now

      try {
        const res = await fetch(`/version.json?_=${now}`, { cache: 'no-store' })
        if (!res.ok) return
        const data = (await res.json()) as { version?: unknown }
        const remote = typeof data.version === 'string' ? data.version : null
        if (!remote) return
        if (remote !== bakedVersion) {
          stoppedRef.current = true
          if (!cancelled) setUpdateAvailable(true)
        }
      } catch {
        // Swallow network / parse errors — try again on next tick.
      }
    }

    const intervalId = window.setInterval(check, POLL_INTERVAL_MS)

    function onVisibility() {
      if (document.visibilityState === 'visible') void check()
    }
    document.addEventListener('visibilitychange', onVisibility)

    return () => {
      cancelled = true
      window.clearInterval(intervalId)
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [bakedVersion])

  return {
    updateAvailable,
    reload: () => window.location.reload(),
  }
}
